import { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { client } from "../../lib/config";
import { toast } from "sonner";
import {
  LayoutDashboard, BedDouble, FileText, HelpCircle, MapPin,
  Star, Route, Calendar, Settings, QrCode, Sparkles, LogOut,
} from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard },
  { to: "/admin/rooms", label: "Rooms", icon: BedDouble },
  { to: "/admin/blog", label: "Blog", icon: FileText },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/attractions", label: "Attractions", icon: MapPin },
  { to: "/admin/towns", label: "Nearby Towns", icon: MapPin },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/redirects", label: "Redirects", icon: Route },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/gbp", label: "GBP Post Calendar", icon: Sparkles },
  { to: "/admin/qr-card", label: "QR Review Card", icon: QrCode },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    client.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const logout = async () => {
    await client.post("/auth/logout").catch(() => {});
    toast.success("Signed out");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <aside className="fixed top-0 left-0 bottom-0 w-64 bg-[#1A1A1A] text-white flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="inline-block bg-[#FDFBF7] border-l-4 border-[#E85D26] py-2 px-3 rounded-sm">
            <img src="/images/logo-horizontal.png" alt="Westbridge Inn & Suites" className="h-8 w-auto" />
          </div>
          <div className="mt-3 text-xs text-white/60 truncate" data-testid="admin-user-email">{user?.email}</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 space-y-1" aria-label="Admin">
          {NAV.map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `mx-3 px-3 py-2 flex items-center gap-3 text-sm font-bold rounded-sm ${
                  isActive ? "bg-[#E85D26] text-white" : "text-white/85 hover:bg-white/10"
                }`
              }
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span className="flex-1">{label}</span>
            </NavLink>
          ))}
        </nav>
        <button
          onClick={logout}
          className="m-3 px-3 py-2 flex items-center gap-3 text-sm font-bold text-white/85 hover:bg-white/10 rounded-sm"
          data-testid="admin-logout"
          style={{ fontFamily: "'Nunito', sans-serif" }}
        >
          <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
        </button>
      </aside>
      <main className="ml-64 min-h-screen">
        <div className="p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
